import React, { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Box,
  Paper,
  TableContainer,
  Button,
  FormControl,
  InputLabel,
  OutlinedInput,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import Label from "../../../components/Label/Label";
import DownloadIcon from "@mui/icons-material/Download";
import styled from "styled-components";

const StyledTableRow = styled(TableRow)`
  &:nth-of-type(even) {
    background-color: #f9f9f9;
  }
  &:last-child td,
  &:last-child th {
    border: 0;
  }
`;

const tableCellStyle = {
  fontWeight: "bold",
  backgroundColor: "#E9E9E9",
  padding: "8px 10px",
  position: "sticky",
  top: 0,
  zIndex: 2,
};

const cellStyle = {
  padding: "8px 10px",
};

const formatHeading = (key) =>
  key
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const TableRowMemo = React.memo(
  ({ row, columns, handleBatchIdClick, index }) => {
    const isChanged = row.changed;

    return (
      <StyledTableRow
        sx={{
          ...(isChanged && {
            backgroundColor: "#ffe6e6 !important",
          }),
        }}
      >
        <TableCell sx={cellStyle}>{index + 1}</TableCell>
        {columns.map((col) => (
          <TableCell key={col} sx={cellStyle}>
            {col === "Batch ID" ? (
              <span
                style={{ color: "#1976d2", cursor: "pointer", fontWeight: 500 }}
                onClick={() => handleBatchIdClick(row["Batch ID"])}
              >
                {row["Batch ID"]}
              </span>
            ) : (
              <Label
                text={row[col]}
                textTransform="none"
                fontWeight="500"
                fontSize="12px"
              />
            )}
          </TableCell>
        ))}
      </StyledTableRow>
    );
  },
);

export default function ResizableTable({
  rawData,
  product,
  site,
  unitOfOperation,
  parameterName,
  handleBatchIdClick,
  uniqueBatches,
}) {
  const [tableType, setTableType] = useState("rawData");

  const columns = useMemo(
    () =>
      tableType === "rawData"
        ? [
            "Parameter Name",
            "Batch ID",
            "Batch Date",
            "value",
            "Unit of Measure",
          ]
        : ["Batch ID", "Batch Date"],
    [tableType],
  );

  const [searchTerm, setSearchTerm] = useState("");

  const activeData = tableType === "rawData" ? rawData : uniqueBatches;

  const filteredData = useMemo(() => {
    if (!Array.isArray(activeData)) return [];
    const term = searchTerm.toLowerCase();
    return activeData.filter((row) =>
      String(row["Batch ID"] || "")
        .toLowerCase()
        .includes(term),
    );
  }, [activeData, searchTerm]);

  const handleExportCSV = () => {
    if (!filteredData?.length) return;

    const csvRows = [columns.join(",")];
    filteredData.forEach((row) => {
      const values = columns.map((col) => row[col] ?? "");
      csvRows.push(values.join(","));
    });

    const csvString = csvRows.join("\n");
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `${parameterName}_${product?.name}_${site?.name}_${unitOfOperation?.name}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const headerCells = useMemo(
    () => [
      <TableCell key="index" sx={tableCellStyle}>
        <Label text="#" fontWeight="500" fontSize="12px" />
      </TableCell>,
      ...columns.map((col) => (
        <TableCell key={col} sx={tableCellStyle}>
          <Label
            text={formatHeading(col)}
            textTransform="none"
            fontWeight="500"
            fontSize="12px"
          />
        </TableCell>
      )),
    ],
    [columns],
  );

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box sx={{ p: 1, display: "flex", justifyContent: "space-between" }}>
        <ToggleButtonGroup
          value={tableType}
          exclusive
          size="small"
          onChange={(e, val) => val && setTableType(val)}
        >
          <ToggleButton value="rawData">
            <Label text="Raw Data" fontSize="12px" fontWeight="500" />
          </ToggleButton>
          <ToggleButton value="uniqueBatches">
            <Label text="Unique Batches" fontSize="12px" fontWeight="500" />
          </ToggleButton>
        </ToggleButtonGroup>

        <Box sx={{ display: "flex", gap: 1 }}>
          <FormControl size="small">
            <InputLabel>Search Batch ID</InputLabel>
            <OutlinedInput
              label="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ height: 35 }}
            />
          </FormControl>

          <Button
            variant="contained"
            disabled={!filteredData?.length}
            onClick={handleExportCSV}
            size="small"
            endIcon={<DownloadIcon />}
            sx={{ height: 35 }}
          >
            <Label text="Download CSV" color="#fff" />
          </Button>
        </Box>
      </Box>

      {/* ✅ EXISTING TABLE (unchanged logic) */}
      <TableContainer
        component={Paper}
        sx={{
          flex: 1,
          borderRadius: 2,
          boxShadow: "none",
          border: "1px solid #e0e0e0",
          overflowY: "auto",
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>{headerCells}</TableRow>
          </TableHead>
          <TableBody>
            {filteredData.length > 0 ? (
              filteredData.map((row, i) => (
                <TableRowMemo
                  key={row["Batch ID"]}
                  row={row}
                  columns={columns}
                  handleBatchIdClick={handleBatchIdClick}
                  index={i}
                />
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length + 1}
                  align="center"
                  sx={cellStyle}
                >
                  <Label text="No matching records found" />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
